import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";
import type {
  FichaDiariaItem,
  UndMedidaOption,
} from "../../../services/controleProducaoService";
import {
  formatQuantidadeComUnd,
  resolveUndFicha,
} from "../../../utils/controleProducaoQuantidade";

type LinhaLocalSobra = {
  sobra: string;
  undMedidaIdSobra: number | "";
  observacaoSobra: string;
};

export type FichaDiariaSobrasPdfInput = {
  lojaNome: string;
  dataISO: string;
  diaSemanaLabel: string;
  itens: FichaDiariaItem[];
  linhas: Record<number, LinhaLocalSobra>;
  unds: UndMedidaOption[];
  justificativaSemSobra?: string | null;
};

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function dataIsoParaNomeArquivo(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
}

function chaveLinha(item: FichaDiariaItem): number {
  return item.lojaIdOutra ? item.itemIdOutraLoja! : item.itemId;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function nomeArquivoSobrasPdf(dataISO: string, lojaNome: string): string {
  const data = dataIsoParaNomeArquivo(dataISO);
  const loja = (lojaNome || "Sem loja").trim().replace(/[\\/:*?"<>|]+/g, "-");
  return `Ficha Diaria Noite - Sobras ${data} - Loja ${loja}.pdf`;
}

export function textoCompartilhamentoSobras(
  dataISO: string,
  lojaNome: string
): string {
  const data = formatISODateToBR(dataISO);
  const loja = lojaNome.trim() || "loja";
  return `FICHA DIÁRIA (NOITE) — Sobras apontadas · ${loja} · ${data}`;
}

function buildSobrasDoc(input: FichaDiariaSobrasPdfInput): jsPDF {
  const primary = hexToRgb(COLORS.PRIMARY);
  const margin = 12;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const docExt = doc as JsPdfWithAutoTable;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 26, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("FICHA DIÁRIA (NOITE)", margin, 11);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Sobras apontadas · Dom Belloni", margin, 18);

  const dataBr = formatISODateToBR(input.dataISO);
  autoTable(doc, {
    startY: 30,
    body: [
      ["Loja", input.lojaNome.trim() || "—"],
      ["Data", `${input.diaSemanaLabel} · ${dataBr}`],
      ["Gerado em", new Date().toLocaleString("pt-BR")],
    ],
    theme: "plain",
    styles: {
      fontSize: 9,
      textColor: [42, 42, 42],
      cellPadding: 1.8,
    },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 28 },
      1: { cellWidth: pageW - margin * 2 - 28 },
    },
    margin: { left: margin, right: margin },
  });

  let totalComSobra = 0;
  const body = input.itens.map((item) => {
    const key = chaveLinha(item);
    const local = input.linhas[key];
    const sobraTxt = (local?.sobra ?? item.sobra ?? "").trim();
    const undSobra = resolveUndFicha(
      item.undMedidaIdBase,
      local?.undMedidaIdSobra ?? item.undMedidaIdSobra
    );
    const sobraFmt = formatQuantidadeComUnd(sobraTxt, undSobra, input.unds);
    const obs = (local?.observacaoSobra ?? item.observacaoSobra ?? "").trim();
    const unidade =
      item.unidadeLabel?.trim() || (item.lojaIdOutra ? "outra unidade" : "");
    if (sobraTxt) totalComSobra += 1;
    return [item.descricao, item.base || "", sobraFmt || "—", obs || "", unidade];
  });

  // Coluna unidade só entra se houver linha de outra loja.
  const temOutraUnidade = input.itens.some((i) => Boolean(i.lojaIdOutra));
  const head = temOutraUnidade
    ? [["Item", "Base", "Sobra", "Obs. sobra", "Unidade"]]
    : [["Item", "Base", "Sobra", "Obs. sobra"]];
  const rows = temOutraUnidade
    ? body
    : body.map(([desc, base, sobra, obs]) => [desc, base, sobra, obs]);

  autoTable(doc, {
    startY: (docExt.lastAutoTable?.finalY ?? 30) + 6,
    head,
    body: rows,
    theme: "striped",
    styles: {
      fontSize: 8,
      cellPadding: 1.6,
      textColor: [42, 42, 42],
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: primary,
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8,
    },
    columnStyles: temOutraUnidade
      ? {
          0: { cellWidth: 62 },
          1: { cellWidth: 26, halign: "right" },
          2: { cellWidth: 26, halign: "right" },
          3: { cellWidth: 42 },
          4: { cellWidth: 28 },
        }
      : {
          0: { cellWidth: 78 },
          1: { cellWidth: 28, halign: "right" },
          2: { cellWidth: 28, halign: "right" },
          3: { cellWidth: "auto" },
        },
    margin: { left: margin, right: margin, bottom: 14 },
  });

  let y = (docExt.lastAutoTable?.finalY ?? 40) + 6;
  const justificativa = input.justificativaSemSobra?.trim();
  if (justificativa) {
    if (y > pageH - 28) {
      doc.addPage();
      y = 14;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(42, 42, 42);
    doc.text("Justificativa (sem sobra)", margin, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(justificativa, pageW - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 4 + 4;
  }

  if (y > pageH - 20) {
    doc.addPage();
    y = 14;
  }
  const totalLinhas = input.itens.length;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text(
    `Resumo: ${totalComSobra} item(ns) com sobra · ${totalLinhas - totalComSobra} sem sobra · ${totalLinhas} no total`,
    margin,
    y
  );

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.text(
      `FICHA DIÁRIA (NOITE) · Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }

  return doc;
}

export async function gerarBlobSobrasPdf(
  input: FichaDiariaSobrasPdfInput
): Promise<{ blob: Blob; filename: string }> {
  const doc = buildSobrasDoc(input);
  const filename = nomeArquivoSobrasPdf(input.dataISO, input.lojaNome);
  const blob = doc.output("blob");
  return { blob, filename };
}

export async function abrirSobrasPdf(
  input: FichaDiariaSobrasPdfInput
): Promise<void> {
  const { blob } = await gerarBlobSobrasPdf(input);
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    downloadBlob(blob, nomeArquivoSobrasPdf(input.dataISO, input.lojaNome));
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function compartilharSobrasPdfWhatsApp(
  input: FichaDiariaSobrasPdfInput
): Promise<"shared" | "downloaded"> {
  const { blob, filename } = await gerarBlobSobrasPdf(input);
  const texto = textoCompartilhamentoSobras(input.dataISO, input.lojaNome);
  const file = new File([blob], filename, { type: "application/pdf" });

  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] });

  if (canShareFiles && typeof navigator.share === "function") {
    await navigator.share({
      files: [file],
      title: "FICHA DIÁRIA (NOITE) — Sobras",
      text: texto,
    });
    return "shared";
  }

  downloadBlob(blob, filename);
  const waUrl = `https://wa.me/?text=${encodeURIComponent(
    `${texto}\n\n(Anexe o PDF que acabou de ser baixado.)`
  )}`;
  window.open(waUrl, "_blank", "noopener,noreferrer");
  return "downloaded";
}
